import _ from 'underscore';
import { useCallback, useContext } from 'react';

import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { AssetGroup, AudienceGroupAssetGroupAssociation } from '@bingads-webui/campaign-odata-schemas';
import { useSuggestionsContext } from '@bingads-webui-campaign-react/pmax-suggestion-service';
import { useWizardService, getFlowFactor, getWizardData, WIZARD_EVENTS } from '@bingads-webui-react/wizard';
import { mapBulkUpsertErrors, getBulkUploadErrorType, genericApiError, ERROR_ENTITY } from '@bingads-webui-campaign-react/pmax-common';
import {
  usePreference,
  preferenceNames,
  useLoadAndUpdateCustomerBusinessInfo,
} from '@bingads-webui-campaign-react/pmax-lite-data';
import {
  readAssetGroup,
  createOrUpdateAssetGroup,
} from '@bingads-webui-campaign-react/perf-max-data';

import { useReadFromSuggestions } from './use-read-from-suggestions';
import { useReadFromCache } from './use-read-from-cache';
import { useAdAssetsInstrument } from './use-ad-assets-instrument';
import { assetGroupModelToViewModel, assetGroupViewModelToModel, getHasAssets } from './model';
import { MinDescriptionCount, MinHeadlineCount, MinLongHeadlineCount } from './const';

export const bulkUpsertAssetGroupSettingsEntityErrorMap = {
  [getBulkUploadErrorType(AssetGroup)]: ERROR_ENTITY.AssetGroup,
  [getBulkUploadErrorType(AudienceGroupAssetGroupAssociation)]: ERROR_ENTITY.AudienceGroupAssetGroupAssociation,
};

export const useAdAssetsRepository = (createAssetGroupWhileLoading) => {
  const { scope } = useContext(ScopeContext);
  const serverContext = useContext(ServerContext);
  const {
    aid, cid, campaignId, assetGroupId,
  } = _.get(scope, ['values'], {});

  const i18n = useContext(I18nContext);

  const adAssetsActivity = useAdAssetsInstrument();

  const { state, send } = useWizardService();
  const isSmartPage = getFlowFactor(state, 'isSmartPage');
  const isCollectBusinessInfo = getFlowFactor(state, 'isCollectBusinessInfo');
  const { cache } = getWizardData(state);

  const {
    loadCustomerContactInfo,
  } = useLoadAndUpdateCustomerBusinessInfo();

  const [{ value: tempImageIdString }] = usePreference(preferenceNames.TempImageIdString);
  const [{ value: websitePreference }, { update: updateWebsitePreference }]
    = usePreference(preferenceNames.WebsiteUrl);
  const [{ value: smartPageUrlPreference }] = usePreference(preferenceNames.SmartPageUrl);

  const onboardingUrlSuggestion = isSmartPage ? smartPageUrlPreference : websitePreference;

  const readFromCache = useReadFromCache({
    createAssetGroupWhileLoading,
    cache,
  });

  const readFromSuggestions = useReadFromSuggestions({
    createAssetGroupWhileLoading,
    onboardingUrlSuggestion,
  });

  const {
    send: suggestionSend,
  } = useSuggestionsContext();

  const readFromMt = useCallback(() => readAssetGroup(cid, aid, campaignId, assetGroupId)
    .then((assetGroupModel) => {
      const assetGroupData = assetGroupModelToViewModel(assetGroupModel, tempImageIdString);

      return {
        onboardingUrl: assetGroupData.FinalUrls[0],
        assetGroupData: {
          ...assetGroupData,
          FinalUrlForRecommendation: assetGroupData.FinalUrls[0],
        },
      };
    }).catch(/* istanbul ignore next */(err) => {
      adAssetsActivity.error(`[Ad assets step] Read from MT failed: ${JSON.stringify(err)}`);

      return Promise.reject(err);
    }), [cid, aid, campaignId, assetGroupId, tempImageIdString, adAssetsActivity]);

  const load = useCallback(() => {
    let loadFn;
    if (assetGroupId) {
      loadFn = readFromMt;
    } else if (cache) {
      loadFn = readFromCache;
    } else if (onboardingUrlSuggestion) {
      loadFn = readFromSuggestions;
    } else {
      loadFn = () => Promise.resolve({
        assetGroupData: {
          Name: 'Default',
          CallToAction: 'ActNow',
        },
      });
    }

    const loadBizInfo = isCollectBusinessInfo ? loadCustomerContactInfo : () => Promise.resolve({});

    return Promise.all([
      loadFn(),
      loadBizInfo(),
    ]).then(([data, businessInfoData]) => {
      const newData = data || {};
      // For collect business info flow, we need to replace the recommend business name
      const businessName = _.get(newData, ['assetGroupData', 'BusinessName'], '');
      if (isCollectBusinessInfo && !_.isEmpty(businessInfoData) && businessName !== businessInfoData.businessName) {
        if (_.has(newData, 'assetGroupData')) {
          newData.assetGroupData.BusinessName = businessInfoData.businessName;
        } else {
          newData.assetGroupData = {
            BusinessName: businessInfoData.businessName,
          };
        }
      }
      // In preview flow, we will create temp asset group if there no suggestion
      // In this case, even when asset group is created, we should prefill the asset group
      if (!getHasAssets(data.assetGroupData)) {
        return {
          ...data,
          shouldPrefill: true,
        };
      }
      return newData;
    }).catch(/* istanbul ignore next */(error) => {
      adAssetsActivity.error(`[Ad assets step] Load failed: ${JSON.stringify(error)}`);

      throw error;
    }).finally(() => {
      send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { isShowingAnimation: false });
    });
  }, [adAssetsActivity, assetGroupId, cache, isCollectBusinessInfo, loadCustomerContactInfo,
    onboardingUrlSuggestion, readFromCache, readFromMt, readFromSuggestions, send]);

  const save = useCallback(({ data, cmpId }) => {
    const { assetGroupData } = data;

    const {
      Images: { ImageList: imageList },
      Descriptions,
      Headlines,
      LongHeadlines,
      BusinessName,
    } = assetGroupData;

    const isDescriptionValid = _.size(_.compact(_.values(Descriptions))) >= MinDescriptionCount;
    const isHeadlineValid = _.size(_.compact(_.values(Headlines))) >= MinHeadlineCount;
    const isLongHeadlineValid = _.size(_.compact(_.values(LongHeadlines))) >= MinLongHeadlineCount;
    const isValid = !_.isEmpty(imageList) && !_.isEmpty(BusinessName)
      && isDescriptionValid && isHeadlineValid && isLongHeadlineValid;

    if (!isValid) {
      return Promise.reject();
    }

    return assetGroupViewModelToModel(assetGroupData, serverContext, cid, aid)
      .then((bulkModel) => {
        const modelToSave = {
          ...bulkModel,
          Campaign: { Id: cmpId || campaignId },
        };
        return createOrUpdateAssetGroup(modelToSave);
      })
      .then((assetGroupModel) => {
        const isUsingSmartPageUrl = assetGroupModel.FinalUrls[0] === smartPageUrlPreference;
        if (!isSmartPage || !isUsingSmartPageUrl) {
          updateWebsitePreference(assetGroupModel.FinalUrls[0]);
          send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { websiteUrl: assetGroupModel.FinalUrls[0] });
        }
        send(WIZARD_EVENTS.UPDATE_FlOW_FACTORS, { flowFactors: { isSmartPage: isUsingSmartPageUrl } });
        if (!onboardingUrlSuggestion) {
          suggestionSend('LOAD', {
            isSmartMode: false,
            isPMaxLite: true,
            url: assetGroupModel.FinalUrls[0],
            language: 'English',
          });
        }
        // clean cache and remove cacheId from scope after asset group is saved
        send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { cache: undefined });
        scope.remove(['cacheId', 'email', 'signature']);
        scope.append({ assetGroupId: assetGroupModel.Id });
        scope.history.replace({
          pathname: scope.history.location.pathname,
          search: scope.stringify(),
        });
        scope.save();

        const dataWithId = {
          ...data,
          assetGroupData: {
            ...data.assetGroupData,
            Id: assetGroupModel.Id,
          },
        };

        return dataWithId;
      })
      .catch(/* istanbul ignore next */(error) => {
        adAssetsActivity.error(`[Ad assets step] Save failed: ${JSON.stringify(error)}`);

        const { errors } = error || {};
        if (!_.isEmpty(errors)) {
          const serverErrors
            = mapBulkUpsertErrors(errors, bulkUpsertAssetGroupSettingsEntityErrorMap, _.identity, null, i18n);

          // eslint-disable-next-line prefer-promise-reject-errors
          return Promise.reject({ error: { serverErrors } });
        }
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({ error: { serverErrors: [genericApiError] } });
      });
  }, [adAssetsActivity, aid, campaignId, cid, i18n, isSmartPage, onboardingUrlSuggestion,
    scope, send, serverContext, smartPageUrlPreference, suggestionSend, updateWebsitePreference]);

  return {
    load,
    save,
  };
};
